// La cuajada contada en tinas, que es como la cuenta quien la hace.
//
// El programa calcula el rendimiento en litros por kilo: 7,02 L/kg. Es la cifra
// correcta y no se toca — pero nadie en una quesera piensa así. Se piensa:
//
//     «una tina de 40 litros me da un queso de 5,7 kilos»
//
// Es exactamente el mismo número (40 ÷ 5,7 = 7,02) dicho en el idioma del
// oficio. Y la diferencia entre los dos idiomas no es de estilo: «rindió 7,87
// en vez de 7,02» no mueve a nadie, y «le faltó queso y medio, son $103.800 de
// leche» hace levantar la cabeza.
//
// Los 40 litros son los de la tina de este cliente, confirmado por él. Va como
// constante y no como ajuste configurable a propósito: no hay todavía ningún
// cliente con una tina distinta, y una pantalla de configuración que nadie abre
// es trabajo que estorba.  El día que aparezca, esto es una línea.

export const LITROS_POR_TINA = 40;

const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Cuántos kilos debería dar una tina, según el rendimiento de esa variedad.
// Es el número con el que se habla: «este queso me da 5,7 por tina».
export function kilosPorTina(rendimientoEsperado, tina = LITROS_POR_TINA) {
  const r = num(rendimientoEsperado);
  if (!r || r <= 0) return null;
  return Math.round((tina / r) * 10) / 10;
}

// El camino de vuelta: de «una tina me da 5,7 kilos» al 7,02 L/kg que guarda
// el programa.
//
// Existe para que nadie tenga que dividir 40 entre 5,7 de cabeza al configurar
// una variedad. El formulario pregunta en kilos por tina —como se habla— y por
// dentro se guarda en litros por kilo, que es como se calcula.
export function rendimientoDeKilos(kilosPorTina, tina = LITROS_POR_TINA) {
  const k = num(kilosPorTina);
  if (!k || k <= 0) return null;
  return Math.round((tina / k) * 100) / 100;
}

// La lectura completa de una cuajada, en tinas y en plata.
//
// Devuelve null cuando falta lo básico. No se devuelven ceros: un «faltaron 0
// kilos» se lee como una afirmación, y no saber no es lo mismo que saber que no.
export function lecturaEnTinas({ litros, kilos, esperado, precioLitro },
                               tina = LITROS_POR_TINA) {
  const L = num(litros), K = num(kilos), R = num(esperado);
  if (!L || L <= 0 || !K || K <= 0) return null;

  const tinas = Math.round((L / tina) * 10) / 10;
  const porTina = kilosPorTina(R, tina);

  if (!R || R <= 0) {
    // Sin rendimiento pactado no hay contra qué comparar. Se dice lo que se
    // sabe —cuántas tinas y cuánto salió— y nada más.
    return { tinas, porTina: null, kilosEsperados: null, diferencia: null,
             quesos: null, litrosDeMas: null, plata: null, rindioMal: false };
  }

  const kilosEsperados = Math.round((L / R) * 10) / 10;
  const diferencia = Math.round((K - kilosEsperados) * 10) / 10;

  // En quesos, que es la unidad con la que se mide el susto: «me faltó un queso
  // y medio» se entiende sin pensar.
  const quesos = porTina ? Math.round((diferencia / porTina) * 10) / 10 : null;

  // La leche que se fue sin volverse queso. Positivo = se gastó de más.
  //
  // Se calcula sobre los kilos REALES, no sobre los esperados: la pregunta es
  // «para estos kilos, ¿cuánta leche debí haber usado?». Restarlo al revés da
  // un número parecido y equivocado.
  const litrosDeMas = Math.round((L - K * R) * 10) / 10;
  const p = num(precioLitro);

  return {
    tinas,
    porTina,
    kilosEsperados,
    diferencia,
    quesos,
    litrosDeMas,
    plata: p && p > 0 ? Math.round(litrosDeMas * p) : null,
    rindioMal: diferencia < 0,
  };
}

// «un queso y medio», «medio queso», «2 quesos». En palabras, porque «1,3
// quesos» obliga a traducir justo cuando se quiere entender rápido.
export function enQuesos(cantidad) {
  const n = Math.abs(num(cantidad) || 0);
  if (n < 0.25) return null;                    // menos de un cuarto: no es noticia
  if (n < 0.75) return 'medio queso';
  if (n < 1.25) return 'un queso';
  if (n < 1.75) return 'un queso y medio';
  const enteros = Math.round(n);
  return `${enteros} quesos`;
}
